import React from 'react';
import { useEntryFinder } from '~Components/EntryFinder';
import { useCurrency, useI18n, useSelector } from '~Hooks';
import { getItemPlaceholderImage } from '../../../../../themes/getTheme';
import SmallItem from '../SmallItems/Item';
import LargeItem from '../LargeItems/Item';
import { LocalItem } from '~Types';
import { checkOutOfStock } from '~Scenes/SelfServiceKiosks/utils/inventory';
import { useInventoryUniverse } from '~Components/InventoryUniverse';
import {
  CompleteExpressQueryKiosk_company_categories_descriptionLang,
  CompleteExpressQueryKiosk_company_categories_items_upsales,
} from '~Scenes/SelfServiceKiosks/Components/useExpressQuery/__queries__';

const SMALL_THUMBNAIL_SIZE = {
  width: 144,
  height: 96,
};

const LARGE_THUMBNAIL_SIZE = {
  width: 468,
  height: 320,
};

export type ImageSize = {
  width: number;
  height: number;
};

interface Props {
  id: string;
  title: string;
  image: string;
  description: CompleteExpressQueryKiosk_company_categories_descriptionLang;
  variations: string[];
  upsales: CompleteExpressQueryKiosk_company_categories_items_upsales[];
  onClick: (id: string, instant?: boolean) => void;
  large?: boolean;
  cartItems: LocalItem[];
}

const GeneralItem = ({
  id,
  title,
  image,
  description,
  variations,
  upsales,
  onClick,
  large,
  cartItems,
}: Props) => {
  const { i18n } = useI18n();
  const findEntryFromItem = useEntryFinder();
  const inventory = useInventoryUniverse();
  const currency = useCurrency();

  const isOutOfStock = checkOutOfStock({
    id,
    inventoryItems: inventory.items,
    disregardCount: false,
    cartItems,
  });

  const companyId = useSelector((state) => state.company);
  const entry = findEntryFromItem(id)!;
  const useImagePlaceholder = !image;
  const imageSize = large ? LARGE_THUMBNAIL_SIZE : SMALL_THUMBNAIL_SIZE;
  const imageUrl = useImagePlaceholder
    ? getItemPlaceholderImage(companyId)
    : `${image}/${imageSize.width * 2}x${imageSize.height * 2}`;

  const addInstantly = Boolean(!i18n(description).length && !variations.length && !upsales.length);

  const handleClick = () => {
    if (isOutOfStock) {
      return;
    }

    onClick(id, entry.instant || addInstantly);
  };

  if (large) {
    return (
      <LargeItem
        onClick={handleClick}
        isInstant={addInstantly}
        title={title}
        price={currency.format(entry.price)}
        imageUrl={imageUrl}
        isUnavailable={isOutOfStock}
        imageSize={imageSize}
      />
    );
  }

  return (
    <SmallItem
      onClick={handleClick}
      isInstant={addInstantly}
      title={title}
      price={currency.format(entry.price)}
      addText={i18n('App.Add')}
      imageUrl={imageUrl}
      isUnavailable={isOutOfStock}
      imageSize={imageSize}
    />
  );
};

export default GeneralItem;
